import { useState, useCallback } from 'react';

interface LedgerState {
  isConnected: boolean;
  isConnecting: boolean;
  address: string | null;
  error: string | null;
}

export const useLedger = () => {
  const [state, setState] = useState<LedgerState>({
    isConnected: false,
    isConnecting: false,
    address: null,
    error: null,
  });

  const connect = useCallback(async () => {
    setState(prev => ({ ...prev, isConnecting: true, error: null }));

    try {
      // Dynamic imports to avoid SSR issues
      const TransportWebHID = (await import('@ledgerhq/hw-transport-webhid')).default;
      const Trx = (await import('@ledgerhq/hw-app-trx')).default;

      // Request permission and create transport
      const transport = await TransportWebHID.create();
      
      // Initialize TRX app
      const trx = new Trx(transport);
      
      // Get address from default derivation path (44'/195'/0'/0/0)
      const result = await trx.getAddress("44'/195'/0'/0/0", false);
      
      // Convert address to base58 format
      const address = result.address;

      setState({
        isConnected: true,
        isConnecting: false,
        address,
        error: null,
      });

      return { address, transport, trx };
    } catch (error: any) {
      console.error('Ledger connection error:', error);
      
      let errorMessage = 'Failed to connect to Ledger';
      
      if (error.message?.includes('No device selected')) {
        errorMessage = 'No Ledger device selected';
      } else if (error.message?.includes('denied')) {
        errorMessage = 'Connection was denied';
      } else if (error.message?.includes('locked')) {
        errorMessage = 'Ledger is locked. Please unlock it.';
      } else if (error.message?.includes('app')) {
        errorMessage = 'Please open TRON app on your Ledger';
      } else if (error.statusCode === 0x6985) {
        errorMessage = 'Transaction rejected on Ledger';
      } else if (error.statusCode === 0x6a80) {
        errorMessage = 'Please open TRON app on Ledger';
      }

      setState({
        isConnected: false,
        isConnecting: false,
        address: null,
        error: errorMessage,
      });

      throw new Error(errorMessage);
    }
  }, []);

  const disconnect = useCallback(() => {
    setState({
      isConnected: false,
      isConnecting: false,
      address: null,
      error: null,
    });
  }, []);

  const signTransaction = useCallback(async (rawTxHex: string) => {
    try {
      const TransportWebHID = (await import('@ledgerhq/hw-transport-webhid')).default;
      const Trx = (await import('@ledgerhq/hw-app-trx')).default;

      const transport = await TransportWebHID.create();
      const trx = new Trx(transport);

      // Sign the transaction
      const signature = await trx.signTransaction(
        "44'/195'/0'/0/0",
        rawTxHex,
        []
      );

      await transport.close();

      return signature;
    } catch (error: any) {
      console.error('Ledger sign error:', error);
      throw error;
    }
  }, []);

  return {
    ...state,
    connect,
    disconnect,
    signTransaction,
  };
};